import { getStyleObjectFromCSS } from '@lexical/selection'
import { cn } from '@navinc/base-react-components'
import { toKebabCase } from '@navinc/utils'
import type {
  SerializedEditorState,
  SerializedElementNode,
  SerializedLexicalNode,
  SerializedRootNode,
  SerializedTextNode,
} from 'lexical'
import { Fragment, JSX, ReactNode } from 'react'

import { AlertBannerBlock } from '@/app/_blocks/AlertBannerBlock/index.jsx'
import { BentoBlock } from '@/app/_blocks/Bento/index.jsx'
import { CalloutBlock } from '@/app/_blocks/CalloutBlock/index.jsx'
import { CarouselBlock } from '@/app/_blocks/Carousel/index.jsx'
import { Disclaimers } from '@/app/_blocks/Disclaimers/index.jsx'
import { FormBlock } from '@/app/_blocks/DynamicForm/index.jsx'
import { FeatureGridBlock } from '@/app/_blocks/FeatureGrid/index.jsx'
import { LegalBlock } from '@/app/_blocks/LegalBlock/index.jsx'
import { OfferBlock } from '@/app/_blocks/OfferBlock/index.jsx'
import { OfferMetaBlock } from '@/app/_blocks/OfferMeta/index.jsx'
import { PriceTierBlock } from '@/app/_blocks/PriceTier/index.jsx'
import { Quotes } from '@/app/_blocks/Quotes/index.jsx'
import { Resources } from '@/app/_blocks/Resources/index.jsx'
import { VideoBlock } from '@/app/_blocks/VideoBlock/index.jsx'
import { VideoEmbedBlock } from '@/app/_blocks/VideoEmbedBlock/index.jsx'

import { Accordion } from '../../_blocks/Accordion/index.jsx'
import { ArchiveBlock } from '../../_blocks/ArchiveBlock/index.jsx'
import { Calculator } from '../../_blocks/Calculator/index.jsx'
import { CallToActionBlock } from '../../_blocks/CallToAction/index.jsx'
import { ContentBlock } from '../../_blocks/Content/index.jsx'
import { CrossLinks } from '../../_blocks/CrossLinks/index.jsx'
import { createExtendedBlocks } from '../../_blocks/Experiment/blocks.jsx'
import { FeatureSection } from '../../_blocks/FeatureSection/index.jsx'
import { ProsCons } from '../../_blocks/ProsConsBlock/index.jsx'
import { CMSLink, CMSLinkProps } from '../Link/index.jsx'
import { NodeFormat } from './nodeFormat.js'

export type RichText = SerializedEditorState<SerializedRootNode>

export const isRichText = (input: unknown): input is RichText => {
  return (
    typeof input === 'object' &&
    input !== null &&
    'root' in input &&
    typeof input.root === 'object' &&
    input.root !== null &&
    'type' in input.root &&
    input.root.type === 'root'
  )
}

const getBlocks = () => {
  return {
    calculator: Calculator,
    cta: CallToActionBlock,
    archive: ArchiveBlock,
    accordion: Accordion,
    content: ContentBlock,
    crossLinks: CrossLinks,
    featureSection: FeatureSection,
    quotes: Quotes,
    carousel: CarouselBlock,
    bento: BentoBlock,
    priceTier: PriceTierBlock,
    form: FormBlock,
    resources: Resources,
    featureGrid: FeatureGridBlock,
    disclaimers: Disclaimers,
    legal: LegalBlock,
    video: VideoBlock,
    offer: OfferBlock,
    callout: CalloutBlock,
    prosCons: ProsCons,
    videoEmbed: VideoEmbedBlock,
    alertBanner: AlertBannerBlock,
  }
}

export type LexicalSerializeNode = (
  | SerializedTextNode
  | SerializedElementNode
  | SerializedRootNode
  | SerializedLexicalNode
) & {
  fields?: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any -- blocks can have many different types
  tag?: keyof JSX.IntrinsicElements
  children?: LexicalSerializeChildren
  value: { [key: string]: any } // eslint-disable-line @typescript-eslint/no-explicit-any -- value can have arbitrary values
  format?: string
  headerState?: number
  templateColumns?: string
  maxWidth?: string | null
  containerAlignment?: string
}

export type LexicalSerializeChildren = LexicalSerializeNode[]

export type NodeClassNameFns = Record<string, (node: LexicalSerializeNode) => string | undefined>

type LexicalSerializeOptions = { nodeClassNames?: NodeClassNameFns }

const getTextAlignClassName = (nodeFormat: string | undefined) => {
  switch (nodeFormat) {
    case 'right':
      return 'text-right'
    case 'center':
      return 'text-center'
    case 'left':
      return 'text-left'
    default:
      return ''
  }
}

let BlocksComponent: ReturnType<typeof createExtendedBlocks<ReturnType<typeof getBlocks>>> | null = null

const nodeTypeConverters: { [key: string]: (node: LexicalSerializeNode, className?: string) => ReactNode } = {
  inlineBlock: (node) => {
    if (node.fields?.blockType === 'offerMeta') {
      return <OfferMetaBlock data={node.fields.dynamicOffer} />
    }
    return null
  },
  block: (node) => {
    if (!node.fields || !node.fields.blockType) {
      return null
    }

    if (!BlocksComponent) {
      BlocksComponent = createExtendedBlocks(getBlocks())
    }

    return (
      <>
        <BlocksComponent blocks={[{ ...node.fields, __typename: node.fields.blockType }]} />
      </>
    )
  },
  upload: (node) => {
    return (
      <div className="w-full overflow-hidden rounded-200">
        <img src={node.value.sizes.largeAutoHeight?.url ?? node.value.url} alt={node.value.alt} />
      </div>
    )
  },
  table: (node) => {
    const rows = node.children
    if (!rows || !rows.length) {
      return null
    }
    return (
      <div className="overflow-hidden rounded-200 border border-outline my-200">
        <div className="overflow-auto w-full">
          <table className="min-w-full border-0">
            <tbody>{serializeLexical(rows)}</tbody>
          </table>
        </div>
      </div>
    )
  },
  tablerow: (node) => (
    <tr className="first:bg-surfaceContainerLowest border-b last:border-b-0">{serializeLexical(node.children)}</tr>
  ),
  tablecell: (node) => (
    <td className="body2 text-onSurface border-r last:border-r-0 align-top">
      <span
        className={cn(
          'flex flex-col p-200',
          node.headerState === 1 && 'font-bold', // bold the text if the cell is a header cell
        )}
      >
        {serializeLexical(node.children)}
      </span>
    </td>
  ),
  heading: (node, className) => {
    if (!node.tag) {
      return null
    }

    const headingClassName = {
      h1: 'display2-emphasized md:display1-emphasized mb-400',
      h2: 'display2-emphasized mb-200',
      h3: 'title1-emphasized mb-200',
      h4: 'title2-emphasized mb-150',
      h5: 'title3-emphasized mb-150',
      h6: 'headline1-emphasized mb-150',
    }[node.tag as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6']
    const HeadingTag = node.tag

    const headingElementAnchorId =
      node.tag === 'h2' && node.children?.[0] && 'text' in node.children[0]
        ? `${toKebabCase(node.children[0].text)}`
        : undefined

    return (
      <HeadingTag
        className={cn(headingClassName, getTextAlignClassName(node.format), className)}
        id={headingElementAnchorId}
      >
        {serializeLexical(node.children)}
      </HeadingTag>
    )
  },
  link: (node) => {
    if (!node.fields) {
      return null
    }

    const { url, linkType, variant, doc, newTab } = node.fields
    return (
      <CMSLink
        type={linkType === 'internal' ? 'reference' : 'custom'}
        url={url}
        variant={(variant ?? 'primaryPlain') as CMSLinkProps['variant']}
        reference={doc as any} // eslint-disable-line @typescript-eslint/no-explicit-any -- TODO determine if we can create a type for this
        newTab={Boolean(newTab)}
        className={cn(
          !variant || variant === 'primaryPlain' || variant === 'plain'
            ? 'inline hover:underline leading-[inherit] text-[length:inherit]'
            : 'inline-flex my-100 w-full md:w-auto',
        )}
      >
        {serializeLexical(node.children)}
      </CMSLink>
    )
  },
  paragraph: (node) => {
    return (
      <p className={cn('w-full text-inherit mb-200 [font-weight:inherit]', getTextAlignClassName(node.format))}>
        {serializeLexical(node.children)}
      </p>
    )
  },
  eyebrow: (node) => {
    return (
      <p
        className={cn(
          'lexical-feature-eyebrow w-full caption1-emphasized uppercase mb-100',
          getTextAlignClassName(node.format),
        )}
      >
        {serializeLexical(node.children)}
      </p>
    )
  },
  list: (node) => {
    if (!node.tag) {
      return null
    }

    const listClassName = {
      ul: 'body1 list-disc text-left inline-flex flex-col mb-200',
      ol: 'body1 list-decimal text-left inline-flex flex-col mb-200',
    }[node.tag as 'ul' | 'ol']

    const ListTag = node.tag
    return (
      <ListTag className={cn(listClassName, getTextAlignClassName(node.format))}>
        {serializeLexical(node.children)}
      </ListTag>
    )
  },
  listitem: (node) => {
    return (
      <li className={cn('body1 mb-100 ml-350', node.children?.[0]?.type === 'list' && 'list-none')}>
        {serializeLexical(node.children)}
      </li>
    )
  },
  'columns-layout-container': (node) => {
    return (
      <div
        className="grid w-full gap-400"
        style={{
          gridTemplateColumns: node.templateColumns,
          maxWidth: node.maxWidth ?? undefined,
          justifySelf: node.containerAlignment,
        }}
      >
        {serializeLexical(node.children)}
      </div>
    )
  },
  'columns-layout-item': (node) => {
    return <div className="w-full">{serializeLexical(node.children)}</div>
  },
  horizontalrule: () => {
    return <hr className="text-outlineVariant" />
  },
  linebreak: () => {
    return <br />
  },
}
export function serializeLexical(children: LexicalSerializeChildren = [], options: LexicalSerializeOptions = {}) {
  return (
    children.map((node, i) => {
      if ('text' in node && node.text) {
        let text: ReactNode = node.text

        const style = getStyleObjectFromCSS(node.style)

        if (node.format & NodeFormat.IS_BOLD) {
          text = <strong className="font-bold">{text}</strong>
        }
        if (node.format & NodeFormat.IS_ITALIC) {
          text = <em className="italic">{text}</em>
        }
        if (node.format & NodeFormat.IS_STRIKETHROUGH) {
          text = <span style={{ textDecoration: 'line-through' }}>{text}</span>
        }
        if (node.format & NodeFormat.IS_UNDERLINE) {
          text = <span style={{ textDecoration: 'underline' }}>{text}</span>
        }
        if (node.format & NodeFormat.IS_CODE) {
          text = <code>{text}</code>
        }
        if (node.format & NodeFormat.IS_SUBSCRIPT) {
          text = <sub>{text}</sub>
        }
        if (node.format & NodeFormat.IS_SUPERSCRIPT) {
          text = <sup>{text}</sup>
        }

        return (
          <span key={i} style={style}>
            {text}
          </span>
        )
      }

      const nodeClassName = options.nodeClassNames?.[node.type]?.(node)

      const converter = nodeTypeConverters[node.type]

      return (
        <Fragment key={i}>
          {converter ? converter(node, nodeClassName) : serializeLexical(node.children, options)}
        </Fragment>
      )
    }) || []
  )
}
